/**
 * 맵 데이터. 지형·스폰·포탈 좌표는 `maps.json`.
 *
 * 좌표계: 월드 좌상단이 (0,0), y 는 아래로 증가.
 * platform 의 (x, y) 는 발판의 좌상단, w 는 가로 길이.
 */

import seed from './maps.json';
import { emitDataReloadSoon, replaceRecord, requireRecord } from './catalog';

export const TIER = { ...seed.TIER };

export interface PlatformDef {
  x: number;
  y: number;
  w: number;
  oneWay?: boolean;
}

export interface ClimbableDef {
  kind: 'ladder' | 'rope';
  x: number;
  yTop: number;
  yBottom: number;
}

export interface PortalDef {
  x: number;
  y: number;
  label: string;
  targetMap: string;
  targetSpawn: string;
}

export interface MonsterSpawnDef {
  monsterId: string;
  x: number;
  y: number;
}

export interface DecorDef {
  texture: string;
  x: number;
  y: number;
  scale?: number;
  alpha?: number;
  flip?: boolean;
}

export interface NpcSpawnDef {
  npcId: string;
  x: number;
  y: number;
}

export interface MapPalette {
  skyTop: number;
  skyBottom: number;
  farHill: number;
  nearHill: number;
  ambient: number;
  ambientAlpha: number;
  cloud: number;
  tileTint: number;
  decorTint: number;
}

export interface MapDef {
  id: string;
  name: string;
  subtitle: string;
  width: number;
  height: number;
  palette: MapPalette;
  bgmMood: 'field' | 'forest';
  spawns: Record<string, { x: number; y: number }>;
  platforms: readonly PlatformDef[];
  climbables: readonly ClimbableDef[];
  portals: readonly PortalDef[];
  monsters: readonly MonsterSpawnDef[];
  decor: readonly DecorDef[];
  npcs: readonly NpcSpawnDef[];
}

export const MAPS: Record<string, MapDef> = structuredClone(seed.MAPS) as unknown as Record<string, MapDef>;
for (const map of Object.values(MAPS)) {
  if (!Array.isArray(map.npcs)) Object.assign(map, { npcs: [] });
}

export let FIRST_MAP = seed.FIRST_MAP;
export let FIRST_SPAWN = seed.FIRST_SPAWN;

export function applyMaps(raw: unknown): void {
  const data = requireRecord(raw, 'maps.json');
  Object.assign(TIER, requireRecord(data.TIER, 'TIER'));
  replaceRecord(MAPS, structuredClone(requireRecord(data.MAPS, 'MAPS')) as unknown as Record<string, MapDef>);
  for (const map of Object.values(MAPS)) {
    if (!Array.isArray(map.npcs)) Object.assign(map, { npcs: [] });
  }
  if (typeof data.FIRST_MAP !== 'string' || typeof data.FIRST_SPAWN !== 'string') {
    throw new Error('maps.json: FIRST_MAP / FIRST_SPAWN 이 없습니다');
  }
  FIRST_MAP = data.FIRST_MAP;
  FIRST_SPAWN = data.FIRST_SPAWN;
}

if (import.meta.hot) {
  import.meta.hot.accept('./maps.json', (mod) => {
    if (!mod?.default) return;
    applyMaps(mod.default);
    emitDataReloadSoon();
  });
}
